import { format } from "date-fns"
import { enUS, fr, type Locale } from "date-fns/locale"

export function getDateFnsLocale(locale: string | undefined): Locale {
  // date-fns ships no Kinyarwanda (rw) locale, so rw — and a missing locale — fall back to English (enUS).
  return locale === "fr" ? fr : enUS
}

/**
 * Compact relative time for recent dates; falls back to an absolute date once older than 30 days.
 * en: "just now" · "2m ago" · "3h ago" · "4d ago" · "2w ago" · "15 Mar" · "15 Mar 2023"
 * fr: "à l'instant" · "il y a 2min" · "il y a 3h" · "il y a 4j" · "il y a 2 sem." · "15 mars" · "15 mars 2023"
 */
export function formatShortRelativeTime(
  dateInput: Date | string | number,
  locale?: string,
): string {
  const date = new Date(dateInput)
  // A timestamp slightly in the future (clock skew) gives a negative difference, which lands in "just now" below.
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  const isFr = locale === "fr"

  if (diffSec < 60) return isFr ? "à l'instant" : "just now"
  if (diffMin < 60) return isFr ? `il y a ${diffMin}min` : `${diffMin}m ago`
  if (diffHour < 24) return isFr ? `il y a ${diffHour}h` : `${diffHour}h ago`
  if (diffDay < 7) return isFr ? `il y a ${diffDay}j` : `${diffDay}d ago`
  // Past a week, "17d" is hard to read at a glance; whole weeks are what people actually say.
  if (diffDay < 30) {
    const diffWeek = Math.floor(diffDay / 7)
    return isFr ? `il y a ${diffWeek} sem.` : `${diffWeek}w ago`
  }

  // Older than 30 days — an absolute date is more readable than "2mo ago" / "1y ago"
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return format(date, sameYear ? "d MMM" : "d MMM yyyy", { locale: getDateFnsLocale(locale) })
}
